// src/pages/reports/SalesReport.js
import React, { useState, useEffect } from "react";
import {
  getSalesReport,
  exportSalesReport,
  getCompanySettings,
} from "../../api/tenant/report.api";
import { toast } from "react-toastify";
import {
  FaFilePdf,
  FaFileCsv,
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
} from "react-icons/fa";

// Suppress native browser alerts for export success messages
const originalAlert = window.alert;
window.alert = function(message) {
  if (message && (message.includes("exported") || message.includes("Sales Report"))) {
    console.log("Suppressed alert:", message);
    return;
  }
  originalAlert(message);
};

const SalesReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Fetch company settings
  const fetchCompanySettings = async () => {
    try {
      const response = await getCompanySettings();
      console.log("Company settings:", response.data);
      if (response.data?.success) {
        setCompanySettings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  // Fetch sales report data
  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const response = await getSalesReport(fromDate, toDate);
      console.log("Sales report response:", response.data);
      
      if (response.data?.success) {
        const data = response.data.data;
        setReportData({
          summary: data.summary?.[0] || {
            total_sales: 0,
            total_gst: 0,
            total_invoices: 0,
            avg_invoice_value: 0,
            unique_customers: 0,
          },
          monthly_data: data.monthly_data || [],
          daily_data: data.daily_data || [],
          top_products: data.top_products || [],
        });
        // Remove this toast.success if you don't want any message
        // toast.success(response.data.message || "Report loaded successfully");
      } else {
        toast.error(response.data?.message || "Failed to fetch sales report");
      }
    } catch (error) {
      console.error("Error fetching sales report:", error);
      toast.error(error.response?.data?.message || "Failed to fetch sales report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    fetchSalesReport();
  }, [fromDate, toDate]);

  // Export handlers - NO SUCCESS TOASTS
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await exportSalesReport("pdf", fromDate, toDate);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `sales_report_${fromDate}_to_${toDate}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // NO toast.success - silent success
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await exportSalesReport("csv", fromDate, toDate);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `sales_report_${fromDate}_to_${toDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // NO toast.success - silent success
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const summary = reportData?.summary || {
    total_sales: 0,
    total_gst: 0,
    total_invoices: 0,
    avg_invoice_value: 0,
    unique_customers: 0,
  };
  const monthlyData = reportData?.monthly_data || [];
  const dailyData = reportData?.daily_data || [];
  const topProducts = reportData?.top_products || [];

  return (
    <div className="container-fluid px-4 py-3">
      {/* Company Header with Logo */}
      {companySettings && (
        <div className="card mb-4 shadow-sm border-0">
          <div className="card-body py-3">
            <div className="row align-items-center">
              <div className="col-auto">
                {companySettings.logo ? (
                  <img
                    src={companySettings.logo}
                    alt={companySettings.company_name}
                    style={{ height: "60px", width: "auto" }}
                  />
                ) : (
                  <FaBuilding size={50} color="#4361ee" />
                )}
              </div>
              <div className="col">
                <h3 className="mb-0 fw-bold" style={{ color: companySettings.primary_color || "#4361ee" }}>
                  {companySettings.company_name || "Business Management Pro"}
                </h3>
                {companySettings.brand_tagline && (
                  <p className="text-muted mb-0">{companySettings.brand_tagline}</p>
                )}
                <small className="text-muted">
                  {companySettings.company_email} | {companySettings.company_phone}
                </small>
              </div>
              <div className="col-auto text-end">
                <div className="text-muted small">
                  <strong>GST:</strong> {companySettings.gst_number || "N/A"}<br />
                  <strong>PAN:</strong> {companySettings.pan_number || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header and Filters */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-0">Sales Report</h2>
          <p className="text-muted mb-0">
            <FaCalendarAlt className="me-1" size={12} />
            {fromDate} to {toDate}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ width: "150px" }}
          />
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ width: "150px" }}
          />
          <button
            onClick={fetchSalesReport}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mb-4">
        <button
          onClick={handleExportPDF}
          className="btn btn-danger me-2"
          disabled={exporting}
        >
          <FaFilePdf className="me-1" />
          {exporting ? "Exporting..." : "Export PDF"}
        </button>
        <button
          onClick={handleExportCSV}
          className="btn btn-success"
          disabled={exporting}
        >
          <FaFileCsv className="me-1" />
          Export CSV
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Fetching sales data...</p>
        </div>
      )}

      {/* Report Content */}
      {!loading && reportData && (
        <>
          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="card bg-primary text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">
                    <FaChartLine className="me-1" /> Total Invoices
                  </h6>
                  <h3 className="mb-0">{summary.total_invoices || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-success text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Sales</h6>
                  <h3 className="mb-0">{formatCurrency(summary.total_sales)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-info text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total GST</h6>
                  <h3 className="mb-0">{formatCurrency(summary.total_gst)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-warning text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Average Invoice</h6>
                  <h3 className="mb-0">{formatCurrency(summary.avg_invoice_value)}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Sales Table */}
          {monthlyData.length > 0 && (
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <h5 className="mb-0">Monthly Sales Summary</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Invoice Count</th>
                        <th className="text-end">Total Sales</th>
                        <th className="text-end">Total GST</th>
                        <th className="text-end">Average Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.map((month, index) => (
                        <tr key={index}>
                          <td>{month.month}</td>
                          <td className="text-end">{month.invoice_count}</td>
                          <td className="text-end">{formatCurrency(month.total_sales)}</td>
                          <td className="text-end">{formatCurrency(month.total_gst)}</td>
                          <td className="text-end">{formatCurrency(month.average_invoice_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Daily Sales Table */}
          {dailyData.length > 0 && (
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <h5 className="mb-0">Daily Sales Details</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Date</th>
                        <th className="text-end">Invoice Count</th>
                        <th className="text-end">Daily Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyData.map((day, index) => (
                        <tr key={index}>
                          <td>{new Date(day.date).toLocaleDateString()}</td>
                          <td className="text-end">{day.invoice_count}</td>
                          <td className="text-end">{formatCurrency(day.daily_sales)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesReport;